import { GridFilterModel, GridLogicOperator } from '@mui/x-data-grid-pro';
import { Gauge } from '@mui/x-charts/Gauge';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';
import {
  BillingKeys,
  getProductAssociations,
  getSubscriptions,
} from '../../api/billing-api';
import {
  CommandDashboardKeys,
  getActiveUsersTileMetrics,
} from '../../api/command-dashboard-api';
import HeroMetric from '../../shared/hero-metric';
import { Messages } from '../../utils/message-constants';
import {
  FULL_WIDTH_TILE_COLUMNS,
  HALF_WIDTH_TILE_COLUMNS,
  SECONDARY_STAT_LABEL_VALUE_GAP,
  SECONDARY_STAT_PADDING,
  TILE_CONTENT_PADDING,
  WIDE_TILE_REGIONS_GAP,
  wideTileRegionSx,
} from './tile-config';
import type { CommandDashboardTileBodyProps } from './tile-content-registry';
import TileInsufficientAccessNotice from './tile-insufficient-access-notice';
import { useCanSeeTile } from './use-tile-access';
import {
  useBindTileRefresh,
  useTrackCommandDashboardTileRefresh,
} from './use-tile-refresh';
import { useTileLinkState } from './use-tile-link-state';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AutoGraph from '@mui/icons-material/AutoGraph';

const HERO_WIDTH = 255;
const NARROW_STAT_PAD_X = 8;
const NARROW_STAT_PAD_Y = 16;
const HERO_BLOCK_INSET_LEFT = 16;
const HERO_DONUT_GAP = 211;
const HERO_ROW_PAD_RIGHT = 40;
const DONUT_SIZE = 220;
const DONUT_INNER_RADIUS = 76;
const DONUT_RING_COLOR = '#3d57bb';
const DONUT_TRACK_COLOR = '#eef1fa';
const SEAT_BADGE_BORDER_COLOR = '#2e9e60';
const SEAT_BADGE_WIDTH = 234;
const DONUT_UNAVAILABLE_INNER_SIZE = 180;
const LICENSE_UNAVAILABLE_TEXT_COLOR = '#233166';

/** One layout per size the tile can be placed at. */
type LayoutTier = 'wide' | 'compact' | 'narrow';

function layoutTierFor(columns: number): LayoutTier {
  if (columns >= FULL_WIDTH_TILE_COLUMNS) {
    return 'wide';
  }
  return columns >= HALF_WIDTH_TILE_COLUMNS ? 'compact' : 'narrow';
}

export const ACTIVE_USERS_STATUS_FILTER: GridFilterModel = {
  items: [{ field: 'status', operator: 'is', value: 'Active' }],
  logicOperator: GridLogicOperator.And,
};

function metricValue(
  showSkeleton: boolean,
  value: number | undefined
): ReactNode {
  return showSkeleton ? <Skeleton width={40} /> : value ?? 0;
}

const SECONDARY_STATS: {
  key: string;
  label: string;
  field: 'suspendedUsers' | 'neverSignedInUsers' | 'archivedUsers';
}[] = [
  { key: 'suspended', label: 'Suspended Users', field: 'suspendedUsers' },
  {
    key: 'neverSignedIn',
    label: 'Never Signed In',
    field: 'neverSignedInUsers',
  },
  { key: 'archived', label: 'Archived Users', field: 'archivedUsers' },
];

export default function ActiveUsersTileMetrics({
  tile,
  columns = FULL_WIDTH_TILE_COLUMNS,
}: CommandDashboardTileBodyProps) {
  const tier = layoutTierFor(columns);
  const hasPermission = useCanSeeTile(tile);

  const query = useQuery({
    queryKey: [CommandDashboardKeys.ActiveUsersTile],
    queryFn: getActiveUsersTileMetrics,
    enabled: hasPermission,
    placeholderData: keepPreviousData,
    meta: { errorMessage: Messages.CommandDashboard.ActiveUsersTileLoadError },
  });
  const metrics = query.data;
  const isLoading = query.isLoading;

  const [isRefreshing, setIsRefreshing] = useState(false);
  const trackRefresh = useTrackCommandDashboardTileRefresh();
  const onRefresh = async () => {
    setIsRefreshing(true);
    try {
      const result = await query.refetch();
      if (!result.isError) {
        trackRefresh();
      }
    } finally {
      setIsRefreshing(false);
    }
  };
  useBindTileRefresh(tile, hasPermission, {
    onRefresh,
    isFetching: query.isFetching,
  });
  useTileLinkState({ presetFilter: ACTIVE_USERS_STATUS_FILTER });

  const showSkeleton = isLoading || isRefreshing;
  const hasMetrics = !showSkeleton && metrics != null;

  const productAssociationsQuery = useQuery({
    queryKey: [BillingKeys.ProductAssociations],
    queryFn: () => getProductAssociations(),
    enabled: hasPermission,
  });
  const subscriptionsQuery = useQuery({
    queryKey: [BillingKeys.Subscriptions],
    queryFn: () => getSubscriptions(productAssociationsQuery.data!),
    enabled: hasPermission && !!productAssociationsQuery.data,
  });

  const totalPurchasedLicenses =
    subscriptionsQuery.data?.totalPurchasedWorkspaceLicenses ?? 0;
  const hasLicenseData = totalPurchasedLicenses > 0;
  // The subscriptions lookup is a two-step chain (productAssociations, then subscriptions once
  // that resolves), so subscriptionsQuery.data stays undefined - and totalPurchasedLicenses stays
  // 0 - through both steps. Without this, the "no license" state briefly (and misleadingly) shows
  // while we're still fetching, before we actually know one way or the other. It only counts as
  // settled once we have a real answer (success) or have given up (either query erroring) -
  // including the case where productAssociationsQuery itself succeeds but with a falsy result
  // (eg. null from the underlying reseller call) - subscriptionsQuery's `enabled` requires
  // truthy data, so it would otherwise never fire and never reach isSuccess/isError, leaving this
  // stuck on "loading" forever instead of falling through to "no license data".
  const productAssociationsUnusable =
    productAssociationsQuery.isSuccess && !productAssociationsQuery.data;
  const isLoadingLicenseData =
    hasPermission &&
    !productAssociationsQuery.isError &&
    !productAssociationsUnusable &&
    !subscriptionsQuery.isError &&
    !subscriptionsQuery.isSuccess;

  if (!hasPermission && metrics === undefined) {
    return <TileInsufficientAccessNotice tile={tile} />;
  }

  const seatPercentage =
    hasMetrics && hasLicenseData
      ? Math.min(
          100,
          Math.round((metrics!.activeUsers / totalPurchasedLicenses) * 100)
        )
      : 0;

  const heroBlock = (
    <Stack
      sx={{
        gap: '24px',
        width: HERO_WIDTH,
        flexShrink: 0,
        ...(tier === 'narrow' && {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0,
          width: '100%',
        }),
      }}
    >
      {/* No "Last Updated" caption: this count is live, not a snapshot. */}
      <HeroMetric
        label="Total Active Users"
        value={metricValue(showSkeleton, metrics?.activeUsers)}
        sx={{ whiteSpace: 'nowrap', minWidth: 0 }}
      />
      {(showSkeleton || isLoadingLicenseData) && (
        <Box
          sx={{
            py: '16px',
            pr: '16px',
            flexShrink: 0,
            ...(tier === 'narrow' && { pr: 0 }),
          }}
        >
          <Skeleton
            variant="rounded"
            width={SEAT_BADGE_WIDTH}
            height={40}
            sx={{ borderRadius: '4px' }}
          />
        </Box>
      )}
      {!showSkeleton && !isLoadingLicenseData && hasLicenseData && (
        <Box
          sx={{
            py: '16px',
            pr: '16px',
            flexShrink: 0,
            ...(tier === 'narrow' && { pr: 0 }),
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'flex-start',
              bgcolor: (theme) => theme.palette.semantic.card.success,
              border: `0.5px solid ${SEAT_BADGE_BORDER_COLOR}`,
              borderRadius: '4px',
              px: '12px',
              py: '8px',
              width: `${SEAT_BADGE_WIDTH}px`,
            }}
          >
            <AutoGraph
              sx={{
                width: '24px',
                height: '24px',
                flexShrink: 0,
                color: (theme) => theme.palette.semantic.metricText.success,
              }}
            />
            <Box
              sx={{
                display: 'flex',
                flex: '1 0 0',
                minWidth: 0,
                alignItems: 'center',
                justifyContent: 'center',
                py: '2px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  lineHeight: 1.43,
                  letterSpacing: '0.17px',
                  whiteSpace: 'nowrap',
                  color: (theme) => theme.palette.semantic.metricText.success,
                }}
              >
                {seatPercentage}% of licensed seats used.
              </Typography>
            </Box>
          </Stack>
        </Box>
      )}
      {!showSkeleton && !isLoadingLicenseData && !hasLicenseData && (
        <Box
          sx={{
            py: '16px',
            pr: '16px',
            flexShrink: 0,
            ...(tier === 'narrow' && { pr: 0 }),
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', width: 'fit-content' }}
          >
            <Box
              sx={{
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: DONUT_TRACK_COLOR,
              }}
            />
            <Typography
              data-testid="active-users-license-unavailable-badge"
              sx={{
                fontSize: '14px',
                lineHeight: 1.43,
                letterSpacing: '0.17px',
                color: (theme) => theme.palette.semantic.text.support,
              }}
            >
              Google Workspace license
              <br />
              managed by Promevo.
            </Typography>
          </Stack>
        </Box>
      )}
    </Stack>
  );

  const donutBlock = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: DONUT_SIZE,
        height: DONUT_SIZE,
        flexShrink: 0,
        ...(tier === 'narrow' && { alignSelf: 'center' }),
      }}
    >
      {showSkeleton ? (
        <Skeleton variant="circular" width={DONUT_SIZE} height={DONUT_SIZE} />
      ) : isLoadingLicenseData ? (
        <CircularProgress size={48} sx={{ color: DONUT_RING_COLOR }} />
      ) : hasLicenseData ? (
        <Gauge
          width={DONUT_SIZE}
          height={DONUT_SIZE}
          innerRadius={DONUT_INNER_RADIUS}
          outerRadius={DONUT_SIZE / 2}
          value={seatPercentage}
          text={({ value }) => `${value}%`}
          sx={{
            '& .MuiGauge-valueArc': { fill: DONUT_RING_COLOR },
            '& .MuiGauge-referenceArc': { fill: DONUT_TRACK_COLOR },
            '& .MuiGauge-valueText text': {
              fontSize: '30px',
              fill: (theme) => theme.palette.semantic.stepper.active,
            },
          }}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: DONUT_SIZE,
            height: DONUT_SIZE,
            borderRadius: '50%',
            bgcolor: DONUT_TRACK_COLOR,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: DONUT_UNAVAILABLE_INNER_SIZE,
              height: DONUT_UNAVAILABLE_INNER_SIZE,
              borderRadius: '50%',
              bgcolor: (theme) => theme.palette.semantic.surface.bg1,
              textAlign: 'center',
              px: '10px',
            }}
          >
            <Typography
              data-testid="active-users-license-unavailable-donut"
              sx={{
                fontSize: '13px',
                lineHeight: '18px',
                letterSpacing: '0.16px',
                color: LICENSE_UNAVAILABLE_TEXT_COLOR,
              }}
            >
              Data unavailable.
              <br />
              This metric requires a
              <br />
              Google Workspace license
              <br />
              managed by Promevo.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );

  const heroSection = (
    <Stack
      direction="row"
      sx={{
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        ...wideTileRegionSx,
        ...(tier !== 'narrow' && { pl: `${HERO_BLOCK_INSET_LEFT}px` }),
        ...(tier !== 'wide' && { flex: 'none', width: '100%' }),
        ...(tier === 'narrow' && { display: 'contents' }),
      }}
    >
      {heroBlock}
      {tier !== 'narrow' && (
        <Box sx={{ flex: `1 1 ${HERO_DONUT_GAP}px`, minWidth: 0 }} />
      )}
      {donutBlock}
      {tier !== 'narrow' && (
        <Box sx={{ flex: `1 1 ${HERO_ROW_PAD_RIGHT}px`, minWidth: 0 }} />
      )}
    </Stack>
  );

  return (
    <Box
      data-testid="active-users-metrics"
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        padding: `${TILE_CONTENT_PADDING}px`,
      }}
    >
      <Stack
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          ...(tier === 'wide'
            ? {
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: `${WIDE_TILE_REGIONS_GAP}px`,
              }
            : {
                flexDirection: 'column',
                alignItems: 'stretch',
                justifyContent: 'space-between',
                gap: 0,
              }),
        }}
      >
        {heroSection}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            ...wideTileRegionSx,
            ...(tier !== 'wide' && { flex: 'none', width: '100%' }),
            ...(tier === 'narrow' && { flexDirection: 'column', gap: 0 }),
          }}
        >
          {SECONDARY_STATS.map((stat) => (
            <Stack
              key={stat.key}
              sx={{
                alignItems: 'flex-start',
                gap: `${SECONDARY_STAT_LABEL_VALUE_GAP}px`,
                flex: '1 1 0',
                minWidth: 0,
                padding: SECONDARY_STAT_PADDING,
                ...(tier === 'narrow' && {
                  flex: 'none',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  gap: 0,
                  padding: `${NARROW_STAT_PAD_Y}px ${NARROW_STAT_PAD_X}px`,
                  borderBottom: (theme) =>
                    `0.5px solid ${theme.palette.semantic.outline.light}`,
                }),
              }}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  lineHeight: 1.5,
                  letterSpacing: '0.15px',
                  color: (theme) => theme.palette.semantic.text.title2,
                }}
              >
                {stat.label}
              </Typography>
              <Typography
                sx={{
                  // Figma's H4, then H5 at the 1/3-width tier - their line heights set each
                  // stat's height, and so how the stats line up.
                  fontSize: '34px',
                  lineHeight: 1.235,
                  letterSpacing: '0.25px',
                  color: (theme) => theme.palette.semantic.stepper.active,
                  ...(tier === 'narrow' && {
                    fontSize: '24px',
                    lineHeight: 1.334,
                    letterSpacing: 0,
                  }),
                }}
              >
                {metricValue(showSkeleton, metrics?.[stat.field])}
              </Typography>
            </Stack>
          ))}
        </Box>
      </Stack>
    </Box>
  );
}
